import React, { useState } from "react";
import { apiCall, useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle, Search } from "lucide-react";

export default function ConvertClientPage() {
  const { user } = useAuth();
  const [userId, setUserId] = useState("");
  const [verified, setVerified] = useState(false);
  const [depositAmount, setDepositAmount] = useState("");
  const [depositDate, setDepositDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(false);

  const checkUserId = async () => {
    const cleanId = userId.replace(/^P-/i, "").trim();
    if (!cleanId) { toast.error("Enter User ID"); return; }
    setChecking(true);
    try {
      // Check if this client exists and agent has called them
      const { data } = await apiCall("get", `/clients/${cleanId}`);
      if (data.client) {
        // Check call logs
        const logsRes = await apiCall("get", `/call-logs?page=1&limit=1`);
        const hasLogs = logsRes.data.logs.some(l => l.client_user_id === cleanId);
        if (!hasLogs && logsRes.data.total > 0) {
          // Check more
          const allLogs = await apiCall("get", `/call-logs?page=1&limit=500`);
          const found = allLogs.data.logs.some(l => l.client_user_id === cleanId);
          if (!found) {
            toast.error(`No call logs found for ${cleanId}. You must call this client first.`);
            setVerified(false);
            setChecking(false);
            return;
          }
        }
        setVerified(true);
        setUserId(cleanId);
        toast.success(`${cleanId} verified! Enter deposit details.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Client not found or not assigned to you");
      setVerified(false);
    }
    finally { setChecking(false); }
  };

  const submitConversion = async () => {
    if (!depositAmount || !depositDate) { toast.error("Enter deposit amount and date"); return; }
    setSubmitting(true);
    try {
      await apiCall("post", "/clients/convert", { client_id: userId, deposit_amount: parseFloat(depositAmount), deposit_date: depositDate });
      toast.success("Conversion submitted for admin approval!");
      setUserId("");
      setVerified(false);
      setDepositAmount("");
      setDepositDate("");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-lg mx-auto space-y-6" data-testid="convert-client-page">
      <div>
        <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Convert Client</h2>
        <p className="text-sm text-muted-foreground mt-1">Enter client User ID to submit conversion for admin approval</p>
      </div>

      <Card className="border border-border">
        <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Step 1: Enter User ID</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Input value={userId} onChange={e => { setUserId(e.target.value); setVerified(false); }} placeholder="USR001" className="h-10" data-testid="convert-userid-input" />
            </div>
            <Button onClick={checkUserId} disabled={checking || !userId.trim()} data-testid="verify-userid-btn">
              <Search className="w-4 h-4 mr-1" /> {checking ? "Checking..." : "Verify"}
            </Button>
          </div>
          {verified && <p className="text-sm text-green-600 font-medium">{userId} verified - call logs found</p>}
        </CardContent>
      </Card>

      {verified && (
        <Card className="border border-primary/30 bg-primary/5">
          <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Step 2: Deposit Details</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div><Label className="text-xs uppercase text-muted-foreground">Deposit Amount (INR)</Label><Input type="number" value={depositAmount} onChange={e => setDepositAmount(e.target.value)} placeholder="Enter amount" className="mt-1 h-10" data-testid="convert-amount-input" /></div>
            <div><Label className="text-xs uppercase text-muted-foreground">Deposit Date</Label><Input type="date" value={depositDate} onChange={e => setDepositDate(e.target.value)} className="mt-1 h-10" data-testid="convert-date-input" /></div>
            <p className="text-xs text-muted-foreground">Calling date will be auto-recorded. Admin will verify and approve.</p>
            <Button className="w-full h-10" onClick={submitConversion} disabled={submitting} data-testid="submit-convert-btn">
              <CheckCircle className="w-4 h-4 mr-2" /> {submitting ? "Submitting..." : "Submit for Approval"}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
